import React, { useMemo } from "react";
import { Box, Link, Typography } from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../../../hooks/storeHooks";
import useCorporateServiceOptions from "../../../../../hooks/useCorporateServiceOptions";
import { useToast } from "../../../../../context/ToasterContext";
import { EventStatusEnum, iEventType } from "../../../../../types/eventTypes";
import { ICorporateEvent, ICorporateEventMessage } from "../../../../../interfaces/corporateEvent";
import AllChatsDetailListItem from "../AllChatsDetailListItem/AllChatsDetailListItem";
import {
	getFromCms,
	convertArrayToString,
	getEventStatusText,
	openGoogleMap,
	removingSecondsFromTime,
	getEventStatusType,
} from "../../../../../helpers/common";
import {
	DATE_FORMAT_MMM_DD_SHORT,
	DEFAULT_DATE_FORMAT,
	addDaysToDate,
	addMonthsToDate,
	fDate,
	formatTimeRange,
} from "../../../../../utils/formatTime";
import { ROUTE_PATHS } from "../../../../../routes";
import GuestSvg from "../../../../../assets/svg-fc/GuestSvg";
import palette from "../../../../../theme/palette";
import typography from "../../../../../theme/typography";
import { EventTypeFilterEnum } from "../../../../../constants/enums";
import CalendarBlank from "../../../../../assets/svg-fc/CalendarBlank";
import MoneyIcon from "../../../../../assets/svg-fc/MoneyIcon";
import { formatBudget, formatPaymentDays, formatServiceTypes } from "../../../../../utils/B2B/corporateEventsUtils";
import { getBudgetValueToDisplay } from "../../../../../pages/Events/utill";
import { iAddress } from "../../../../../types/eventTypes";
import { IEventDetailMessages } from "../../../../../types/eventTypes";
import EventOtherFieldValue, {
	getCmsPresetDisplayValue,
} from "../../../../reusable-components/event-other-field-value/EventOtherFieldValue";
import "./EventDetailsFields.scss";

interface EventDetailsFieldsProps {
	eventDetail: iEventType | ICorporateEvent;
	chatMessage: IEventDetailMessages | ICorporateEventMessage;
	eventType: EventTypeFilterEnum;
}

const EventDetailsFields: React.FC<EventDetailsFieldsProps> = ({ eventDetail, chatMessage, eventType }) => {
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const cms = useAppSelector((state) => state.events.cms);
	const serviceOptions = useCorporateServiceOptions();

	const isB2B = eventType === EventTypeFilterEnum.B2B;
	const isShopify = eventType === EventTypeFilterEnum.Prepaid;
	const isB2C = !isB2B && !isShopify;

	// Check if event is editable (only for B2C)
	const isEventEditable = useMemo(() => {
		if (isB2B) return false;
		const b2cEvent = eventDetail as iEventType;
		return b2cEvent.status === EventStatusEnum.Active || b2cEvent.status === EventStatusEnum.Accepted; // Active or Accepted
	}, [isB2B, eventDetail]);

	const isBudgetEditable = useMemo(() => {
		if (isB2B) return false;
		const b2cEvent = eventDetail as iEventType;
		return b2cEvent.status === EventStatusEnum.Active; // Active only
	}, [isB2B, eventDetail]);

	const renderVenue = (eventAddress: iAddress, addressString?: string): React.ReactNode => {
		return (
			<Link
				component="button"
				variant="body2"
				onClick={() => {
					if (eventAddress?.place_name) {
						openGoogleMap(eventAddress);
					}
				}}
				sx={{
					textDecoration: "underline",
					cursor: "pointer",
					padding: 0,
					border: "none",
					background: "none",
					font: "inherit",
					width: "fit-content",
					textAlign: "start",
					color: palette.grey[1150],
					fontFamily: typography.fontFamily,
					fontWeight: typography.fontWeightNormal,
				}}
			>
				{addressString ||
					[eventAddress?.place_name || "", eventAddress?.addressLine2 || ""]
						.filter((str) => !!str)
						.join(", ")
						.trim() ||
					"None"}
			</Link>
		);
	};

	const renderEventName = (name: string, date: string) => {
		const type = getEventStatusType(eventDetail.status, eventType);
		const link =
			eventType === EventTypeFilterEnum.B2B
				? `${ROUTE_PATHS.B2B.EVENT_DETAIL.replace(":id", eventDetail.id)}?type=${type}`
				: `${ROUTE_PATHS.EVENT_DETAIL.replace(":id", eventDetail.id)}?type=${type}`;

		return (
			<Box>
				<Link
					href={link}
					target="_blank"
					rel="noopener noreferrer"
					style={{
						fontSize: "18px",
						fontWeight: typography.fontWeightNormal,
						fontFamily: typography.fontSecondary,
						margin: 0,
						color: palette.grey[1150],
						display: "-webkit-box",
						WebkitLineClamp: 2,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
						textOverflow: "ellipsis",
						cursor: "pointer",
						textDecoration: "none",
					}}
				>
					{name} - {fDate(date, DATE_FORMAT_MMM_DD_SHORT)}
				</Link>
			</Box>
		);
	};

	const fields = useMemo(() => {
		if (isB2B) {
			const b2bEvent = eventDetail as ICorporateEvent;
			return [
				{
					title: "",
					value: `${b2bEvent.name} - ${fDate(b2bEvent.startDate, DATE_FORMAT_MMM_DD_SHORT)}` || "None",
					renderValue: () => renderEventName(b2bEvent.name, b2bEvent.startDate),
				},
				{
					title: "Order ID",
					value: b2bEvent.orderNumber?.toString() || "None",
				},
				{
					title: "Start Date",
					icon: <CalendarBlank />,
					value: b2bEvent.startDate ? fDate(b2bEvent.startDate, DEFAULT_DATE_FORMAT) : "None",
				},
				{
					title: "Event Days",
					value: b2bEvent.numberOfDays
						? `${b2bEvent.numberOfDays} day${b2bEvent.numberOfDays > 1 ? "s" : ""}`
						: "None",
				},
				{
					title: "Service Type",
					value: formatServiceTypes(b2bEvent.serviceTypes, serviceOptions, "None"),
				},
				{
					title: "Deadline to quote",
					value: b2bEvent.externalDeadline ? fDate(b2bEvent.externalDeadline, DEFAULT_DATE_FORMAT) : "None",
				},
				{
					title: "Budget",
					icon: <MoneyIcon />,
					value: b2bEvent.budget ? formatBudget(b2bEvent?.budget) : "N/A",
				},
				{
					title: "Payment Terms (From Event Start Date)",
					value: formatPaymentDays(b2bEvent?.paymentDays),
				},
			];
		} else if (isShopify) {
			const shopifyEvent = eventDetail as iEventType;
			return [
				{
					title: "",
					value:
						`${shopifyEvent.name} - ${fDate(shopifyEvent.eventDate, DATE_FORMAT_MMM_DD_SHORT)}` || "None",
					renderValue: () => renderEventName(shopifyEvent.name, shopifyEvent.eventDate),
				},
				{
					title: "Order ID",
					value: shopifyEvent.orderNumber?.toString() || "None",
				},
				{
					title: "Event Date",
					icon: <CalendarBlank />,
					value: shopifyEvent.eventDate ? fDate(shopifyEvent.eventDate, DEFAULT_DATE_FORMAT) : "None",
				},
				{
					title: "Event Emirate",
					value: shopifyEvent.city ? getFromCms(shopifyEvent.city + "", cms) : "None",
				},
				{
					title: "Budget",
					icon: <MoneyIcon />,
					value: formatBudget(shopifyEvent?.budget),
					editable: false,
				},
				{
					title: "No. of Guests",
					icon: <GuestSvg />,
					value: shopifyEvent.guestsCount?.toString() || "None",
				},
				{
					title: "Start Time",
					value: formatTimeRange(
						removingSecondsFromTime(shopifyEvent.startTime),
						removingSecondsFromTime(shopifyEvent.endTime),
						" - ",
					),
				},
				shopifyEvent?.address?.place_name
					? {
							title: "Venue",
							renderValue: () => renderVenue(shopifyEvent?.address),
						}
					: null,
			];
		} else {
			// B2C Events
			const b2cEvent = eventDetail as iEventType;
			const otherCuisineText = b2cEvent?.otherKindOfFoods?.value?.trim() || "";
			const otherCoursesText = b2cEvent?.otherCourses?.value?.trim() || "";
			const cuisinePresets = getCmsPresetDisplayValue(
				(b2cEvent?.typeOfFoods || []).concat(b2cEvent?.typeOfCuisines || []),
				cms,
			);
			const coursesPresets = getCmsPresetDisplayValue(b2cEvent?.courses, cms);
			return [
				{
					title: "",
					value: `${b2cEvent.name} - ${fDate(b2cEvent.eventDate, DATE_FORMAT_MMM_DD_SHORT)}` || "None",
					renderValue: () => renderEventName(b2cEvent.name, b2cEvent.eventDate),
				},
				{
					title: "Order ID",
					value: b2cEvent.orderNumber?.toString() || "None",
				},

				{
					title: "Event Date",
					icon: <CalendarBlank />,
					value: b2cEvent.eventDate ? fDate(b2cEvent.eventDate, DEFAULT_DATE_FORMAT) : "None",
					editable: isEventEditable,
					inputType: "date" as const,
					min: addDaysToDate(1),
					max: addMonthsToDate(8),
				},
				{
					title: "Number of Guests",
					value: b2cEvent.guestsCount?.toString() || "None",
					editable: isEventEditable,
					inputType: "number" as const,
					min: 1,
					maxLength: 4,
					icon: <GuestSvg />,
					errorMessage: "Number of guests must be at least 1",
				},
				{
					title: "Budget",
					icon: <MoneyIcon />,
					value: formatBudget(Number(getBudgetValueToDisplay(b2cEvent))),
					flexible: b2cEvent?.isBudgetFlexible,
					editable: isBudgetEditable,
					inputType: "number",
					min: 1,
					errorMessage: "Budget must be greater than 0",
				},
				{
					title: "Event Type",
					value: getFromCms(b2cEvent.serviceCategory?.value, cms) || "None",
				},
				{
					title: "Event Time",
					value: getFromCms(b2cEvent?.timeOfDay?.value, cms),
				},
				{
					title: "Provider Type",
					value: getFromCms(b2cEvent?.providerType?.value, cms),
				},
				{
					title: "Service Type",
					value: b2cEvent?.providerOptions?.value
						? getFromCms(b2cEvent?.providerOptions?.value, cms)
						: convertArrayToString(b2cEvent?.providerOptionsMultiple, cms),
				},
				{
					title: "Cuisine Type",
					renderValue: () => (
						<EventOtherFieldValue
							presetValue={cuisinePresets}
							otherText={otherCuisineText}
							otherLabel="Other cuisine"
						/>
					),
				},
				{
					title: "Courses",
					renderValue: () => (
						<EventOtherFieldValue
							presetValue={coursesPresets}
							otherText={otherCoursesText}
							otherLabel="Other courses"
						/>
					),
				},
				Boolean(b2cEvent?.dishType) && {
					title: "Special Instructions",
					value: b2cEvent?.dishType,
				},
				{
					title: "Location",
					value: getFromCms(b2cEvent?.city + "", cms),
				},
				!!b2cEvent?.address?.place_name && {
					title: "Venue",
					value:
						[
							b2cEvent?.address?.place_name || "",
							b2cEvent?.address?.addressLine2 || "",
							getFromCms(b2cEvent?.city + "", cms) || "",
						]
							.filter((str) => !!str)
							.join(", ")
							.trim() || "None",
					renderValue: () =>
						renderVenue(
							b2cEvent?.address,
							[
								b2cEvent?.address?.place_name || "",
								b2cEvent?.address?.addressLine2 || "",
								getFromCms(b2cEvent?.city + "", cms) || "",
							]
								.filter((str) => !!str)
								.join(", ")
								.trim() || "None",
						),
				},
			];
		}
	}, [
		eventDetail,
		cms,
		serviceOptions,
		eventType,
		isEventEditable,
		isBudgetEditable,
		isB2B,
		isShopify,
		isB2C,
		dispatch,
		showToast,
	]);

	return (
		<Box className="eventDetailsFields">
			{fields.map((field, index) => (
				<AllChatsDetailListItem
					key={index}
					{...(field && "title" in field && field.title && { title: field.title })}
					{...(field && "value" in field && field.value && { value: field.value })}
					{...(field && "icon" in field && field.icon && { icon: field.icon })}
					{...(field && "renderValue" in field && field.renderValue && { renderValue: field.renderValue })}
					{...(field && "editable" in field && field.editable !== undefined && { editable: field.editable })}
					{...(field && "inputType" in field && field.inputType && { inputType: field.inputType })}
					{...(field && "min" in field && field.min !== undefined && { min: field.min })}
					{...(field && "max" in field && field.max !== undefined && { max: field.max })}
					{...(field &&
						"maxLength" in field &&
						field.maxLength !== undefined && { maxLength: field.maxLength })}
					{...(field &&
						"errorMessage" in field &&
						field.errorMessage && { errorMessage: field.errorMessage })}
				/>
			))}
		</Box>
	);
};

export default EventDetailsFields;
